import cloneDeep from 'lodash/cloneDeep'
import React, { useEffect, useState } from 'react'
import { Circle, Group, Line, Rect } from 'react-konva'

const predefinedColorsVehicle = [
    'MintCream',
    'GreenYellow',
    'Chartreuse',
    'LawnGreen',
    'Lime',
    'LimeGreen',
    'PaleGreen',
    'LightGreen',
    'MediumSpringGreen',
    'SpringGreen',
    'MediumSeaGreen',
    'SeaGreen',
    'ForestGreen',
    'Green',
    'OliveDrab',
    'Olive',
    'DarkOliveGreen',
    'MediumAquamarine',
    'MediumTurquoise',
    'LightSeaGreen',
    'MediumSeaGreen',
    'MediumAquamarine',
    'GreenYellow',
    'LimeGreen',
    'Chartreuse',
    'SpringGreen',
    'MediumSpringGreen',
    'YellowGreen',
    'PaleGreen',
    'LightGreen'
]

const predefinedColorsLP = [
    '#FFEBEE',
    '#FFCDD2',
    '#EF9A9A',
    '#E57373',
    '#EF5350',
    '#F44336',
    '#E53935',
    '#D32F2F',
    '#C62828',
    '#B71C1C',
    '#FF8A80',
    '#FF5252',
    '#FF1744',
    '#D50000',
    '#FF6E40',
    '#FF3D00',
    '#DD2C00',
    '#E64A19',
    '#FF6E40',
    '#FF3D00',
    '#DD2C00',
    '#E64A19',
    '#FF6E40',
    '#FF3D00',
    '#DD2C00',
    '#E64A19',
    '#FF6E40',
    '#FF3D00',
    '#DD2C00',
    '#E64A19',
    '#DD2C00'
]

function normalizeInputData(trajectoryData: any, imageWidth: number, imageHeight: number) {
    return trajectoryData.map((data: any) => {
        const normalizedData = cloneDeep(data)

        if (data.trajectory.x) {
            normalizedData.trajectory.x = data.trajectory.x.map((data_x: number) => {
                const normalizedValue = (data_x / imageWidth) * 750
                return Math.min(750, Math.max(0, normalizedValue)).toFixed(1)
            })
        }

        if (data.trajectory.y) {
            normalizedData.trajectory.y = data.trajectory.y.map((data_y: number) => {
                const normalizedValue = (data_y / imageHeight) * (750 * (imageHeight / imageWidth))
                return Math.min(750, Math.max(0, normalizedValue)).toFixed(1)
            })
        }

        if (data.best_detection) {
            const x = (data.best_detection.x / imageWidth) * 750
            const y = (data.best_detection.y / imageHeight) * (750 * (imageHeight / imageWidth))
            const width = (data.best_detection.width / imageWidth) * 750
            const height = (data.best_detection.height / imageHeight) * (750 * (imageHeight / imageWidth))
            normalizedData.best_detection = {
                x: Math.min(750, Math.max(0, x)).toFixed(1),
                y: Math.min(750, Math.max(0, y)).toFixed(1),
                width: Math.min(750, Math.max(0, width)).toFixed(1),
                height: Math.min(750, Math.max(0, height)).toFixed(1)
            }
        }
        return normalizedData
    })
}

const TrajectoryMemorized: React.FC<{
    Data: any
    numberOfTrajectory: number
    identifier: string
    best_detection: number
}> = React.memo(({ Data, numberOfTrajectory, identifier, best_detection }) => {
    // console.log('rendering: ', identifier, ' ', numberOfTrajectory)
    const Trajectory: JSX.Element[] = []
    let new_trajectory = true
    let prevX, prevY
    let index_key = numberOfTrajectory

    const xData = Data[numberOfTrajectory].trajectory.x
    const yData = Data[numberOfTrajectory].trajectory.y
    const bestDetectionData = Data[numberOfTrajectory].best_detection

    let color
    let color_line
    if (identifier === 'vehicle') {
        color = predefinedColorsVehicle[numberOfTrajectory]
        color_line = '#E8F5E9'
        if (best_detection === 1 && bestDetectionData) {
            const centerX = Number(bestDetectionData.x)
            const centerY = Number(bestDetectionData.y)
            const width = Number(bestDetectionData.width)
            const height = Number(bestDetectionData.height)
            const x = centerX - width / 2
            const y = centerY - height / 2
            Trajectory.push(
                <Rect
                    key={`best-detection-vehicle-${index_key}`}
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill="transparent"
                    stroke="yellow"
                    strokeWidth={2}
                />
            )
        }
    } else {
        color = predefinedColorsLP[numberOfTrajectory]
        color_line = '#FFD180'
        if (best_detection === 1 && bestDetectionData) {
            const centerX = Number(bestDetectionData.x)
            const centerY = Number(bestDetectionData.y)
            const width = Number(bestDetectionData.width)
            const height = Number(bestDetectionData.height)
            const x = centerX - width / 2
            const y = centerY - height / 2
            Trajectory.push(
                <Rect
                    key={`best-detection-vehicle-${index_key}`}
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill="transparent"
                    stroke="blue"
                    strokeWidth={2}
                />
            )
        }
    }

    for (let index = 0; index < xData.length; index++) {
        const x = Number(xData[index])
        const y = Number(yData[index])

        if (prevX !== undefined && prevY !== undefined) {
            if (!new_trajectory) {
                Trajectory.push(
                    <Line
                        key={`line-${index_key}`}
                        points={[prevX, prevY, x, y]}
                        stroke={color_line}
                        strokeWidth={1.5}
                    />
                )
            }
        }
        if (identifier === 'vehicle') {
            Trajectory.push(
                <Rect
                    key={`rect-${index_key}`}
                    x={x - 5}
                    y={y - 5}
                    width={10}
                    height={10}
                    fill="transparent"
                    stroke={color}
                    strokeWidth={2}
                />
            )
        } else {
            Trajectory.push(
                <Circle
                    key={`circle-${index_key}`}
                    x={x}
                    y={y}
                    radius={5}
                    strokeWidth={2}
                    fill="transparent"
                    stroke={color}
                />
            )
        }
        prevX = x
        prevY = y
        index_key += 1
        new_trajectory = false
    }
    new_trajectory = true
    return <Group>{Trajectory}</Group>
})

const TrajectoryViewLayer: React.FC<{
    trajectory_data: any
    trajectories_vehicle: any
    trajectories_lp: any
    best_detection: number
    enable_view: boolean
}> = (props) => {
    const [vehicleTrajectoriesToRender, setVehicleTrajectoriesToRender] = useState<any>([])
    const [lpTrajectoriesToRender, setLpTrajectoriesToRender] = useState<any>([])
    const [normalizedData, setNormalizedData] = useState<{
        vehicleData: JSX.Element[]
        lpData: JSX.Element[]
    }>({
        vehicleData: [],
        lpData: []
    })

    useEffect(() => {
        const vehicleRenderTemp: number[] = []
        for (let i = 0; i <= 30; i++) {
            if (
                i > props.trajectories_vehicle.view_trajectories[0] &&
                i <= props.trajectories_vehicle.view_trajectories[1]
            ) {
                vehicleRenderTemp.push(i - 1)
            }
        }
        setVehicleTrajectoriesToRender(vehicleRenderTemp)
    }, [props.trajectories_vehicle.view_trajectories])

    useEffect(() => {
        const lpRenderTemp: number[] = []
        for (let i = 0; i <= 30; i++) {
            if (i > props.trajectories_lp.view_trajectories[0] && i <= props.trajectories_lp.view_trajectories[1]) {
                lpRenderTemp.push(i - 1)
            }
        }
        setLpTrajectoriesToRender(lpRenderTemp)
    }, [props.trajectories_lp.view_trajectories])

    useEffect(() => {
        const image_size = { x: 1920, y: 1080 }
        const normalized_data_vehicle = normalizeInputData(
            props.trajectory_data.vehicle_trajectories,
            image_size.x,
            image_size.y
        )
        const normalized_data_lp = normalizeInputData(props.trajectory_data.lp_trajectories, image_size.x, image_size.y)

        setNormalizedData({
            vehicleData: normalized_data_vehicle,
            lpData: normalized_data_lp
        })
    }, [props.trajectory_data])

    if (!props.enable_view || normalizedData.vehicleData.length === 0 || normalizedData.lpData.length === 0) {
        return null
    }
    return (
        <Group>
            {vehicleTrajectoriesToRender.map((id: number) => (
                <Group key={`vehicle-${id}`}>
                    <TrajectoryMemorized
                        key={`trajectory-${id}`}
                        Data={normalizedData.vehicleData}
                        numberOfTrajectory={id}
                        identifier={'vehicle'}
                        best_detection={props.best_detection}
                    />
                </Group>
            ))}
            {lpTrajectoriesToRender.map((id: number) => (
                <Group key={`lp-${id}`}>
                    <TrajectoryMemorized
                        key={`trajectory-${id}`}
                        Data={normalizedData.lpData}
                        numberOfTrajectory={id}
                        identifier={'lp'}
                        best_detection={props.best_detection}
                    />
                </Group>
            ))}
        </Group>
    )
}

export default TrajectoryViewLayer
